import { ArticleVariant, PaginatedResponse, Article, LeagueTable, Match, PlayerLineup } from '@repo/types';
import { Image } from '@ui/common/components/Image';
import ArticleListUI from '@ui/article/ArticleList/ArticleListUI';
import FixtureListUI from '@ui/fixtures/components/FixtureList/FixtureListUI';
import LeagueTableUI from '@ui/league/LeagueTable/LeagueTableUI';
import { LeagueTableColumn } from '@ui/league/LeagueTable/leagueTableColumns';
import LinkButton from '@ui/common/components/Link/Link';
import { LinkVariant, LinkSize } from '@ui/common/components/Link/contracts';

// @ts-ignore fix vite import type
const isLeagueSite = import.meta.env.VITE_SITE_TYPE === 'league';

type TeamTabProps = {
  team: Match['homeTeam'] | Match['awayTeam'];
  lineups?: PlayerLineup[];
  subs?: PlayerLineup[];
  leagueTable?: LeagueTable;
  results?: PaginatedResponse<Match>;
  fixtures?: PaginatedResponse<Match>;
  articles?: PaginatedResponse<Article>;
};

export const TeamTab = ({ team, lineups, subs, leagueTable, results, fixtures, articles }: TeamTabProps) => {
  return (
    <>
      <div className="match-overview__lineups-container">
        <div className="match-overview__lineups-lists-container">
          <div
            className="match-overview__lineups-list-wrapper match-overview__lineups-list-wrapper--home"
            style={{ marginLeft: 'auto' }}
          >
            <ul className="match-overview__lineups-list">
              {lineups?.map((lineup, index) => (
                <li className="match-overview__lineups-list-item">
                  <div>
                    <span className="match-overview__lineups-player-shirt-number">{lineup.shirtNumber}</span>
                    <span className="match-overview__lineups-player-name">
                      {lineup.player.firstName} {lineup.player.lastName} {index === 0 ? '(GK)' : ''}
                      {index === 0 && lineup.isCaptain ? ' ' : ''}
                      {lineup.isCaptain ? `(C)` : ''}
                    </span>
                  </div>
                  <div className="match-overview__lineups-player-info-container">
                    {lineup.goals?.map((goal) => (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                        <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                      </div>
                    ))}
                    {lineup.replacedBy && (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{lineup.replacedBy.minute}'</span>
                        <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                      </div>
                    )}
                    {lineup.yellowCards &&
                      lineup.yellowCards.map((minute) => (
                        <div className="match-overview__lineups-player-sub-container">
                          <span className="match-overview__lineups-player-sub-minute">{minute}'</span>
                          <div className={'match-overview__yellow-card'} />
                        </div>
                      ))}
                    {lineup.redCard && (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{lineup.redCard}'</span>
                        <div className={'match-overview__red-card'} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <hr style={{ width: '100%' }} />
            <ul className="match-overview__lineups-list">
              {subs?.map((sub) => (
                <li className="match-overview__lineups-list-item">
                  <div>
                    <span className="match-overview__lineups-player-shirt-number">{sub.shirtNumber}</span>
                    <span className="match-overview__lineups-player-name">
                      {sub.player.firstName} {sub.player.lastName}
                    </span>
                  </div>
                  <div className="match-overview__lineups-player-info-container">
                    {sub.goals?.map((goal) => (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{goal.minute}'</span>
                        <Image src="/ball-icon.svg" alt="goal" width={20} height={20} />
                      </div>
                    ))}
                    {sub.replaces && (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{sub.replaces.minute}'</span>
                        <Image src="/loop-icon.svg" alt="substitution" width={20} height={20} />
                      </div>
                    )}
                    {sub.yellowCards &&
                      sub.yellowCards.map((minute) => (
                        <div className="match-overview__lineups-player-sub-container">
                          <span className="match-overview__lineups-player-sub-minute">{minute}'</span>
                          <div className={'match-overview__yellow-card'} />
                        </div>
                      ))}
                    {sub.redCard && (
                      <div className="match-overview__lineups-player-sub-container">
                        <span className="match-overview__lineups-player-sub-minute">{sub.redCard}'</span>
                        <div className={'match-overview__red-card'} />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {leagueTable?.competition && (
        <div className="match-overview__news-container">
          <div className="match-overview__league-table-list-container">
            <div className="match-overview__news-team-container match-overview__news-team-container--left">
              <LeagueTableUI
                leagueTable={leagueTable}
                limit={3}
                highlightedClubId={team.id}
                columns={[
                  LeagueTableColumn.Rank,
                  LeagueTableColumn.Team,
                  LeagueTableColumn.GP,
                  LeagueTableColumn.W,
                  LeagueTableColumn.D,
                  LeagueTableColumn.L,
                  LeagueTableColumn.GD,
                  LeagueTableColumn.P,
                ]}
                title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">${leagueTable.competition.name}</h2><hr/>`}
              />
            </div>
          </div>
        </div>
      )}

      {results && Boolean(results?.items?.length) && (
        <div className="match-overview__news-container">
          <div className="match-overview__news-list-container">
            <div className="match-overview__news-team-container match-overview__news-team-container--left">
              <FixtureListUI
                fixtures={results.items}
                sortByDate={'DESC'}
                groupByDate={true}
                columns={1}
                linkLabel="VIEW ALL"
                linkUrl={`/results?team=${team?.id}`}
                linkSize={LinkSize.sm}
                linkVariant={LinkVariant.Text}
                title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Recent results</h2><hr/>`}
              />
            </div>
          </div>
        </div>
      )}

      {fixtures && Boolean(fixtures?.items?.length) && (
        <div className="match-overview__news-container">
          <div className="match-overview__news-list-container">
            <div className="match-overview__news-team-container match-overview__news-team-container--left">
              <FixtureListUI
                fixtures={fixtures.items}
                sortByDate={'ASC'}
                groupByDate={true}
                columns={1}
                linkLabel="VIEW ALL"
                linkUrl={`/results?team=${team?.id}`}
                linkSize={LinkSize.sm}
                linkVariant={LinkVariant.Text}
                title={`<h2 style="display: flex; justify-content: center; margin-bottom: 10px;">Upcoming fixtures</h2><hr/>`}
              />
            </div>
          </div>
        </div>
      )}

      {articles && Boolean(articles?.items?.length) && (
        <div className="match-overview__news-container">
          <div className="match-overview__news-list-container">
            <div className="match-overview__news-team-container">
              <div>
                <h2
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '10px',
                  }}
                >
                  Latest news
                </h2>
                <hr />
              </div>
              <ArticleListUI
                paginatedArticles={articles}
                variant={ArticleVariant.TextSideBySide}
                showDate={false}
                columns={1}
              />
              <LinkButton
                label={`More ${team.shortName} news`}
                url={isLeagueSite ? `/news?tags=${team?.club?.slug}` : `/news?tags=${team?.slug}`}
                openInNewTab={false}
                variant={LinkVariant.Text}
                size={LinkSize.sm}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
